/**
 * Plain-English names for the ids the Case screen would otherwise print as if
 * they were words: pipeline stages (`s1_parse`) and event-log names
 * (`docir.built`).
 *
 * Both maps are written out by hand from the server's own vocabulary — the
 * `STAGE_*` and `EVENT_*` constants in `server/app/services/events.py` — and
 * nothing is derived from an id's spelling. A label made by tidying the id
 * ("Docir built") would read as plain English and still be an internal word.
 *
 * An id this file does not know comes back unchanged. The server may add a
 * stage or an event before this file learns about it, and the one thing a
 * reader of an audit log must never lose is the entry itself: a raw id on
 * screen is ugly, a missing or blank one is a hole in the record. Callers keep
 * the raw id in a `title` attribute so the exact name is one hover away
 * whether or not it was mapped.
 *
 * Vitest cannot see the Python constants, so nothing here is checked against
 * the server: when a constant is added there, add it here too.
 */

/** `STAGE_*` in `server/app/services/events.py`. */
const STAGE_LABELS: ReadonlyMap<string, string> = new Map([
  ['s0_intake', 'Intake'],
  ['s1_parse', 'Reading the document'],
  ['s1b_marks', 'Reading the marks'],
  ['s2_normalize', 'Grounds and deadline'],
  ['s3_analyze', 'Analysis'],
  ['s4_research', 'Research'],
  ['s5_generate', 'Drafting'],
  ['s6_review', 'Review'],
  // Not a pipeline stage: what a person did to the matter (closing it,
  // reassigning it, a note), filed under no stage on purpose.
  ['system', 'Matter'],
])

/** `EVENT_*` in `server/app/services/events.py`. */
const EVENT_LABELS: ReadonlyMap<string, string> = new Map([
  ['matter.created', 'Matter created'],
  ['matter.queued', 'Matter queued'],
  ['matter.reassigned', 'Matter reassigned'],
  ['matter.lifecycle_changed', 'Matter status changed'],
  ['matter.declined', 'Matter declined'],
  ['agent.assigned', 'Agent assigned'],
  ['facts.confirmed', 'Facts confirmed'],
  ['facts.corrected', 'Facts corrected'],
  ['deadline.reconciled', 'Deadline checked'],
  ['deadline.verified', 'Deadline verified'],
  ['docir.built', 'Document read'],
  ['marks.extracted', 'Marks read'],
  ['grounds.classified', 'Grounds identified'],
  ['document.analyzed', 'Document analyzed'],
  ['ledger.verified', 'Citations checked'],
  ['pack.rendered', 'Draft prepared'],
  ['flag.raised', 'Flag raised'],
  ['flag.resolved', 'Flag resolved'],
  ['override.recorded', 'Override recorded'],
  ['note.recorded', 'Note added'],
  ['research.curated', 'Web page reviewed'],
  ['draft.signed', 'Draft signed off'],
  ['draft.exported', 'Draft exported'],
  ['assessment_pack.rendered', 'Assessment pack prepared'],
  ['assessment_pack.approved', 'Assessment pack approved'],
  ['assessment_pack.dispatched', 'Assessment pack dispatched'],
  ['bd_gate.cleared', 'Business sign-off given'],
])

/** A pipeline stage id as a short plain name, or the id itself when unmapped. */
export function stageLabel(id: string): string {
  return STAGE_LABELS.get(id) ?? id
}

/**
 * An event-log name as a short plain phrase, or the name itself when unmapped.
 *
 * `stage` is the stage the event was filed under, and only `pack.rendered`
 * reads it. The runner writes that event twice: at `s5_generate` when a draft
 * was produced, and at `s6_review` when the gate found nothing (a clean review
 * has no flag to raise, so it files the same event). Read alone it would say
 * "Draft prepared" on a review row, and on a checks-only run, where no draft
 * exists, that would be false.
 */
export function eventLabel(id: string, stage?: string): string {
  if (id === 'pack.rendered' && stage === 's6_review') return 'Review passed, no findings'
  return EVENT_LABELS.get(id) ?? id
}
